import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";

/**
 * Dark, app-like theme. `index.html` sets `class="dark"` on <html>, so
 * Chakra's own components (inputs, radios, spinners) use their dark
 * styles too. Core's login, password and loading screens are drawn for a
 * dark page, so they fit as they are.
 */
const config = defineConfig({
  globalCss: {
    "html, body": {
      backgroundColor: "#0e0a0a",
      color: "white",
      overscrollBehavior: "none",
      WebkitTapHighlightColor: "transparent",
    },
    "*::-webkit-scrollbar": { display: "none" },
    "*": { scrollbarWidth: "none" },
  },
  theme: {
    keyframes: {
      tlRise: {
        from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
        to: { opacity: 1, transform: "translateY(0) scale(1)" },
      },
      tlFade: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      tlSheetUp: {
        from: { transform: "translateY(100%)" },
        to: { transform: "translateY(0)" },
      },
      tlDot: {
        "0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.4 },
        "30%": { transform: "translateY(-4px)", opacity: 1 },
      },
      tlIslandIn: {
        from: {
          opacity: 0,
          transform: "translateY(10px)",
          filter: "blur(4px)",
        },
        to: { opacity: 1, transform: "translateY(0)", filter: "blur(0)" },
      },
      tlGlow: {
        "0%, 100%": { opacity: 0.55 },
        "50%": { opacity: 0.85 },
      },
    },
    tokens: {
      fonts: {
        heading: {
          value:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, system-ui, sans-serif",
        },
        body: {
          value:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, system-ui, sans-serif",
        },
      },
      colors: {
        brand: {
          50: { value: "#fff1f0" },
          100: { value: "#ffdfdd" },
          200: { value: "#ffc4c0" },
          300: { value: "#ff9b94" },
          400: { value: "#ff6257" },
          500: { value: "#ff3123" },
          600: { value: "#ff291a" },
          700: { value: "#d70e00" },
          800: { value: "#b10e03" },
          900: { value: "#92130a" },
        },
        rose: {
          200: { value: "#e6cbc5" },
          400: { value: "#c49c94" },
        },
      },
    },
    semanticTokens: {
      colors: {
        app: { value: "#0e0a0a" },
        surface: { value: "#191313" },
        elevated: { value: "#231b1a" },
        line: { value: "rgba(255,255,255,0.08)" },
        muted: { value: "rgba(255,255,255,0.6)" },
        faint: { value: "rgba(255,255,255,0.4)" },
        accent: { value: "{colors.brand.400}" },
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);

/** `#rrggbb` + alpha (0-1) → `rgba(...)`, for companion colour washes. */
export function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** The accent gradient used for primary buttons and the user's bubbles. */
export const ACCENT_GRADIENT =
  "linear-gradient(135deg, #ff6257 0%, #ff8a7a 100%)";
